import type { LucideIcon } from 'lucide-react';
import {
  Zap,
  Cpu,
  Palette,
  Gamepad2,
  Heart,
  Activity,
  Home,
  Mountain,
  Users,
  Coffee,
  Camera,
  Sparkles,
  PawPrint,
  BookOpen,
  Package,
  Star,
} from 'lucide-react';

export interface Category {
  id: number;
  name: string;
  limit: number;
  description: string;
  emoji: string;
  isExpandable?: boolean;
  icon?: LucideIcon;
  iconBg?: string;
  iconText?: string;
  excludedFromMonthlyLimit?: boolean;
  userSelectable?: boolean;
}

export const categories: Category[] = [
  {
    id: 1,
    name: 'X-Team Events',
    limit: 2500,
    emoji: '✈️',
    description:
      'Enjoy epic experiences with your fellow X-Teamers. Get reimbursed for flights, food, activities, and extra expenses incurred during Outposts and Summits. Valid reimbursements: bit.ly/unleash-events',
    isExpandable: true,
    icon: Zap,
    iconBg: 'bg-yellow-100',
    iconText: 'text-yellow-600',
    excludedFromMonthlyLimit: true,
  },
  {
    id: 2,
    name: 'Tech & Gadgets',
    limit: 200,
    emoji: '📊',
    description:
      'This category is all about levelling up your tech game for fun and creativity. Think Wi-Fi routers that make you forget buffering, audio gear that brings your playlist to life, and gadgets that supercharge your space.',
    icon: Cpu,
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
  },
  {
    id: 3,
    name: 'Creativity',
    limit: 200,
    emoji: '🎨',
    description:
      'Unleash your creative genius! Grab streaming gear for gaming sessions, DIY projects, art masterpieces, or creative subscriptions. Get ready to let your creativity run wild!',
    icon: Palette,
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
  },
  {
    id: 4,
    name: 'Entertainment',
    limit: 200,
    emoji: '🍿',
    description:
      'Turn ordinary days into memorable nights! Rock concerts, theatre, comedy shows, movie nights, dance extravaganzas, museum visits – all fair game for a reimbursement party!',
    icon: Gamepad2,
    iconBg: 'bg-fuchsia-100',
    iconText: 'text-fuchsia-600',
  },
  {
    id: 5,
    name: 'Health & Wellness',
    limit: 200,
    emoji: '❤️',
    description:
      'Prioritise your well-being! From getting professional support, coworking vibes to boost productivity and wellness apps for peak performance, to team happy hours. Keep your work-life balance rocking!',
    icon: Heart,
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
  },
  {
    id: 6,
    name: 'Active Lifestyle',
    limit: 200,
    emoji: '🏃',
    description:
      "Keep adventure alive! Whether it's bike rentals for urban exploration, gym or any other sport-related memberships for that workout groove, or gear for your adrenaline-pumping adventures, this category has your active lifestyle expenses covered.",
    icon: Activity,
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-600',
  },
  {
    id: 7,
    name: 'Home & Decor',
    limit: 200,
    emoji: '🏠',
    description:
      "Elevate your Home & Decor game! Whether it's comfy ergonomic gear, cooking equipment and sustainable products, or smart home items, decorations and house cleaning services, make your home sweet home even sweeter.",
    icon: Home,
    iconBg: 'bg-stone-100',
    iconText: 'text-stone-600',
  },
  {
    id: 8,
    name: 'Outdoor Adventures',
    limit: 200,
    emoji: '🏔️',
    description:
      "Hiking, camping, or exploring new horizons - this category covers your outdoor escapades. Don't forget to expense your luggage and travel accessories for those epic adventures, Airbnb, car rentals or fuel.",
    icon: Mountain,
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
  },
  {
    id: 9,
    name: 'Family, Kids & Friends',
    limit: 200,
    emoji: '👨‍👩‍👧‍👦',
    description:
      "Time to spread the love. We're here to help you cherish those precious bonds. Cover babysitters, host meetups that bring joy, fund scholarships for bright futures, and send care packages that light up someone's day.",
    icon: Users,
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600',
  },
  {
    id: 10,
    name: 'Food & Drinks',
    limit: 200,
    emoji: '☕',
    description:
      "Coffee lovers, foodies, and fresh produce enthusiasts, this one's for you. Get ready to expense that gourmet coffee, indulge in fresh produce, and dine like a connoisseur.",
    icon: Coffee,
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
  },
  {
    id: 11,
    name: 'Hobbies',
    limit: 200,
    emoji: '📷',
    description:
      "Dive into your passions with a big smile. Whether it's photography, music, gaming, or collecting treasures, your hobbies matter. Get reimbursed for the gear that makes your heart race.",
    icon: Camera,
    iconBg: 'bg-pink-100',
    iconText: 'text-pink-600',
  },
  {
    id: 12,
    name: 'Self-Expression & Personal Care',
    limit: 200,
    emoji: '💄',
    description:
      'Express your unique style! This category is your ticket to showcase your individuality. Unleash self-expression items like clothing, accessories, and tattoos. Treat yourself to beauty products and spa sessions.',
    icon: Sparkles,
    iconBg: 'bg-violet-100',
    iconText: 'text-violet-600',
  },
  {
    id: 13,
    name: 'Furry Friends',
    limit: 200,
    emoji: '🐾',
    description:
      'Fur-babies are family too! Treat your pets like royalty with grooming services, high-quality pet food, pet sitting expenses, toys or any other thing they might need. They deserve the best, and so do you.',
    icon: PawPrint,
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-800',
  },
  {
    id: 14,
    name: 'Learning',
    limit: 2500,
    emoji: '📚',
    description:
      "Knowledge is power. Grab books, courses, and conference tickets to fuel your mind and supercharge your skills. Your journey to self-improvement starts here, and we'll back you up.",
    icon: BookOpen,
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
    excludedFromMonthlyLimit: true,
  },
  {
    id: 15,
    name: 'Parcel Forwarding/Fees',
    limit: 100,
    emoji: '📦',
    description:
      'If you need a more reliable way to get things like Vault items shipped to you, use this category to get a mail forwarder, or to cover import fees.',
    icon: Package,
    iconBg: 'bg-zinc-100',
    iconText: 'text-zinc-600',
  },
  {
    id: 16,
    name: 'Approved Exception',
    limit: 0,
    emoji: '⭐',
    description: 'An exception approved by the Unleash team. Only admins can assign this category.',
    icon: Star,
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    excludedFromMonthlyLimit: true,
    userSelectable: false,
  },
];

export const EXCLUDED_FROM_MONTHLY_LIMIT_IDS = categories
  .filter((cat) => cat.excludedFromMonthlyLimit)
  .map((cat) => cat.id);
